import { Card, IconButton, Space, WithTooltip } from '@procurify/ui'

import { PageActions } from '@webapp/core/components/Page'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseViewerRoles } from '@webapp/modules/expense/components/ExpenseItemTable'
import { EditExpenseLineItemDrawer } from '@webapp/modules/expense/feature-sets/EditExpenseLineItemDrawer'
import { ExpenseCommentPopoverButton } from '@webapp/modules/expense/feature-sets/ExpenseView/components'
import { EditExpenseReportPresenter } from '@webapp/modules/expense/features/edit-expense-report/presenters/EditExpenseReportPresenter'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ApprovalReminderIconButton } from '@webapp/modules/expense/features/send-approval-reminder/presenters/ApprovalReminderIconButton'
import messages from './messages'
import { ExpenseEditViewStyled } from './styles/ExpenseEditViewStyled'
import { useExpenseReportEditView } from './useExpenseReportEditView'

export interface IExpenseReportEditViewProps {
  uuid: string
}

export const ExpenseReportEditView = ({
  uuid,
}: IExpenseReportEditViewProps) => {
  const intl = useProcurifyIntl()

  const {
    data: { expense, editingItem, drawerOpen },
    states: { isRequester, showComments, expenseContentTypeId, userId },
    methods: {
      onLineItemEditClick,
      onLineItemEditSuccess,
      onLineItemEditCancel,
      onEditSuccess,
      onEditCancel,
      onSubmittingChange,
    },
    commands: { duplicateExpense },
  } = useExpenseReportEditView({ uuid })

  const hasLogs = !!expense?.hasLogs
  const nextApprover = expense?.nextApprover ?? null
  const showSendReminder =
    isRequester && !!expense?.isPending && nextApprover !== null

  return (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
              />
            </WithTooltip>
          )}

          {hasLogs && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}

          {showSendReminder && expense && nextApprover && (
            <ApprovalReminderIconButton
              expenseId={expense.id}
              recipient={nextApprover}
            />
          )}
        </Space>
      </PageActions>

      <ExpenseEditViewStyled>
        <Card>
          <Card.Content>
            <Space size='large'>
              {expense && (
                <EditExpenseReportPresenter
                  expense={expense}
                  onSuccess={onEditSuccess}
                  onEditItem={onLineItemEditClick}
                  onSubmittingChange={onSubmittingChange}
                  onCancel={onEditCancel}
                  viewerRole={
                    isRequester
                      ? ExpenseViewerRoles.REQUESTER
                      : ExpenseViewerRoles.APPROVER
                  }
                />
              )}
            </Space>
          </Card.Content>
        </Card>
      </ExpenseEditViewStyled>

      {editingItem && expense && (
        <EditExpenseLineItemDrawer
          drawerOpen={drawerOpen}
          onClose={onLineItemEditCancel}
          item={editingItem}
          expenseReportId={expense.id}
          requesterId={expense.requester.id}
          secondaryCategoryId={expense.secondaryCategory.id}
          onSuccess={onLineItemEditSuccess}
        />
      )}
    </>
  )
}

ExpenseReportEditView.displayName = 'ExpenseReportEditView'
